import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from '#app/components/ui/avatar.tsx'
import { cn, getInitials } from '#app/utils/misc.tsx'

type Props = {
	onClick?: (
		event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
	) => void
	avatar: { alt: string; src?: string; fallback?: ReactNode }
	title: ReactNode
	subtitle?: ReactNode
	subtitleClassName?: string
	actions?: ReactNode
	className?: string
	innerClassName?: string
	ariaPressed?: boolean
}

export function ListItem({
	avatar,
	title,
	subtitle,
	subtitleClassName,
	actions,
	className,
	innerClassName,
	onClick,
	ariaPressed,
}: Props) {
	const clickable = typeof onClick === 'function'

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (!clickable) return
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
			onClick?.(event)
		}
	}

	const handleActionClick = (event: MouseEvent<HTMLDivElement>) => {
		event.stopPropagation()
	}

	const handleActionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar')
			return
		event.stopPropagation()
	}

	return (
		<div
			className={cn(
				'rounded-lg border bg-white p-3',
				clickable ? 'hover:bg-muted/60 cursor-pointer transition' : undefined,
				className,
			)}
			role={clickable ? 'button' : undefined}
			aria-disabled={clickable ? undefined : true}
			data-clickable={clickable ? '' : undefined}
			tabIndex={clickable ? 0 : undefined}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			aria-pressed={
				clickable && typeof ariaPressed === 'boolean' ? ariaPressed : undefined
			}
		>
			<div
				className={cn(
					'flex items-center justify-between gap-3',
					innerClassName,
				)}
			>
				<div className="flex min-w-0 items-center gap-3">
					<Avatar className="h-8 w-8 rounded-lg">
						<AvatarImage src={avatar.src} alt={avatar.alt} />
						<AvatarFallback className="rounded-lg text-xs font-normal">
							{avatar.fallback ?? getInitials(avatar.alt)}
						</AvatarFallback>
					</Avatar>
					<div className="min-w-0 leading-tight">
						<div className="flex min-w-0 items-center gap-1 text-sm font-medium">
							{title}
						</div>
						{subtitle ? (
							<div
								className={cn(
									'text-muted-foreground line-clamp-1 text-xs break-words',
									subtitleClassName,
								)}
							>
								{subtitle}
							</div>
						) : null}
					</div>
				</div>
				{actions ? (
					<div
						className="flex flex-shrink-0 items-center gap-2"
						onClick={handleActionClick}
						onKeyDown={handleActionKeyDown}
						onPointerDownCapture={(event) => event.stopPropagation()}
					>
						{actions}
					</div>
				) : null}
			</div>
		</div>
	)
}
